import React from 'react';

import type { OrderStageLookup } from '../lib/orderStages';
import './LifecycleStageTracker.css';

export type LifecycleStageStep<TStatus extends string = string> = {
  key: string;
  label: string;
  targetStatus: TStatus | null;
  /** Optional second line under the label ("Oct 5", "22 of 22"); only Warehouse records set it. */
  detail?: string | null;
};

type LifecycleStageTrackerProps<TStatus extends string = string> = {
  steps: ReadonlyArray<LifecycleStageStep<TStatus>>;
  currentStepKey: string | null;
  currentStatusLabel?: string | null;
  ariaLabel: string;
  onSelect?: (status: TStatus, step: LifecycleStageStep<TStatus>) => void;
  disabled?: boolean;
  disabledReason?: string | null;
  busy?: boolean;
  pendingStepKey?: string | null;
  pulseCurrentStep?: boolean;
  error?: string | null;
  exceptionLabel?: string | null;
  className?: string;
};

const FIT_STEP_COUNT = 5;

type TrackerStyle = React.CSSProperties & {
  '--lifecycle-step-count': number;
};

export type EventMilestoneDefinition = {
  key: string;
  label: string;
  statuses: readonly string[];
  targetPreference: readonly string[];
};

/**
 * The client-facing event lifecycle. Detailed warehouse statuses remain the
 * source of truth; this list only groups them into the client-safe milestones a
 * person can scan in a record header.
 */
export const CLIENT_EVENT_LIFECYCLE_MILESTONES: readonly EventMilestoneDefinition[] = [
  {
    key: 'quote',
    label: 'Quote In Progress',
    statuses: ['quote', 'budgetary', 'approval_pending'],
    targetPreference: ['approval_pending', 'quote', 'budgetary'],
  },
  {
    key: 'pending_acceptance',
    label: 'Pending Acceptance',
    statuses: ['approved', 'pending_acceptance'],
    targetPreference: ['approved', 'pending_acceptance'],
  },
  {
    key: 'confirmed',
    label: 'Confirmed',
    statuses: ['confirmed'],
    targetPreference: ['confirmed'],
  },
  {
    key: 'preparing',
    label: 'Preparing',
    statuses: ['picking', 'partially_filled', 'filled'],
    targetPreference: ['partially_filled', 'filled'],
  },
  {
    key: 'in_progress',
    label: 'Event in Progress',
    statuses: ['partially_shipped', 'shipped', 'on_rent'],
    targetPreference: ['on_rent', 'shipped', 'partially_shipped'],
  },
  {
    key: 'returning',
    label: 'Returning',
    statuses: ['partially_returned', 'returning', 'returned'],
    targetPreference: ['partially_returned', 'returned'],
  },
  {
    key: 'complete',
    label: 'Complete',
    statuses: ['ready_to_invoice', 'invoiced', 'closed'],
    targetPreference: ['ready_to_invoice', 'invoiced', 'closed'],
  },
] as const;

/** @deprecated Use CLIENT_EVENT_LIFECYCLE_MILESTONES for client-facing progress. */
export const EVENT_LIFECYCLE_MILESTONES = CLIENT_EVENT_LIFECYCLE_MILESTONES;

function normalizedStatus(value: string | null | undefined): string {
  return String(value ?? '').trim().toLowerCase();
}

const FINAL_EVENT_LIFECYCLE_STATUSES = new Set(['invoiced', 'lost', 'cancelled', 'void']);

export function isEventLifecycleFinalStage(status: string | null | undefined): boolean {
  return FINAL_EVENT_LIFECYCLE_STATUSES.has(normalizedStatus(status));
}

function lookupHasStatus(lookup: OrderStageLookup, status: string): boolean {
  const normalized = normalizedStatus(status);
  return Object.keys(lookup).some(key => normalizedStatus(key) === normalized);
}

function lookupStatusValue(lookup: OrderStageLookup, status: string): string | null {
  const normalized = normalizedStatus(status);
  return Object.keys(lookup).find(key => normalizedStatus(key) === normalized) ?? null;
}

export function buildEventLifecycleSteps(
  lookup: OrderStageLookup,
): Array<LifecycleStageStep<string>> {
  return CLIENT_EVENT_LIFECYCLE_MILESTONES.map(milestone => {
    const target = milestone.targetPreference
      .find(status => lookupHasStatus(lookup, status));
    return {
      key: milestone.key,
      label: milestone.label,
      targetStatus: target ? lookupStatusValue(lookup, target) : null,
    };
  });
}

export function getEventLifecycleStepKey(status: string | null | undefined): string | null {
  const normalized = normalizedStatus(status);
  if (!normalized) return null;
  return CLIENT_EVENT_LIFECYCLE_MILESTONES.find(milestone => (
    milestone.statuses.some(value => normalizedStatus(value) === normalized)
  ))?.key ?? null;
}

export type InternalLifecycleOptions = {
  includeBudgetary?: boolean;
  terminalStatus?: string | null;
  statusBeforeTerminal?: string | null;
};

const INTERNAL_TERMINAL_STATUSES = new Set(['lost', 'cancelled', 'void']);
const HIDDEN_INTERNAL_LIFECYCLE_STATUSES = new Set(['picking', 'returning']);

function internalLifecycleDisplayStatus(status: string | null | undefined): string {
  const normalized = normalizedStatus(status);
  if (normalized === 'picking') return 'confirmed';
  if (normalized === 'returning') return 'partially_returned';
  return normalized;
}

/**
 * Internal users work with the configured database stages directly, except
 * for optional/exception branches. Budgetary appears only after a budgetary
 * quote has been submitted. Lost and Cancelled appear only when the event has
 * actually ended, immediately after the last active stage it reached.
 */
export function buildInternalEventLifecycleSteps(
  lookup: OrderStageLookup,
  options: InternalLifecycleOptions = {},
): Array<LifecycleStageStep<string>> {
  const terminalKey = normalizedStatus(options.terminalStatus);
  const priorKey = internalLifecycleDisplayStatus(options.statusBeforeTerminal);
  const includeBudgetary = options.includeBudgetary || priorKey === 'budgetary';
  const configured = Object.entries(lookup)
    .map(([status, entry]) => ({
      key: normalizedStatus(status),
      label: entry.label?.trim() || status
        .split('_')
        .filter(Boolean)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' '),
      targetStatus: status,
      stageIndex: entry.stageIndex,
    }))
    .sort((left, right) => (
      left.stageIndex - right.stageIndex
      || left.label.localeCompare(right.label)
    ));
  const activeStages = configured.filter(stage => (
    !INTERNAL_TERMINAL_STATUSES.has(stage.key)
    && !HIDDEN_INTERNAL_LIFECYCLE_STATUSES.has(stage.key)
    && (stage.key !== 'budgetary' || includeBudgetary)
  ));

  if (!INTERNAL_TERMINAL_STATUSES.has(terminalKey)) {
    return activeStages.map(({ key, label, targetStatus }) => ({ key, label, targetStatus }));
  }

  const terminalStage = configured.find(stage => stage.key === terminalKey) ?? {
    key: terminalKey,
    label: terminalKey === 'cancelled' ? 'Cancelled' : 'Lost',
    targetStatus: terminalKey,
    stageIndex: Number.MAX_SAFE_INTEGER,
  };
  const priorIndex = activeStages.findIndex(stage => stage.key === priorKey);
  const completedPath = priorIndex >= 0 ? activeStages.slice(0, priorIndex + 1) : activeStages;
  return [...completedPath, terminalStage]
    .map(({ key, label, targetStatus }) => ({ key, label, targetStatus }));
}

export function getInternalEventLifecycleStepKey(
  status: string | null | undefined,
  lookup: OrderStageLookup,
): string | null {
  const normalized = internalLifecycleDisplayStatus(status);
  if (!normalized) return null;
  return Object.keys(lookup).some(key => normalizedStatus(key) === normalized)
    ? normalized
    : null;
}

export function getLifecycleExceptionLabel(status: string | null | undefined): string | null {
  const normalized = normalizedStatus(status);
  if (!normalized) return null;
  if (normalized === 'lost' || normalized === 'void') return 'Lost';
  if (normalized === 'cancelled') return 'Cancelled';
  return null;
}

export function getClientLifecycleExceptionLabel(status: string | null | undefined): string | null {
  const normalized = normalizedStatus(status);
  if (normalized === 'lost' || normalized === 'void') return 'Event not proceeding';
  if (normalized === 'cancelled') return 'Event cancelled';
  return null;
}

export default function LifecycleStageTracker<TStatus extends string = string>({
  steps,
  currentStepKey,
  currentStatusLabel,
  ariaLabel,
  onSelect,
  disabled = false,
  disabledReason,
  busy = false,
  pendingStepKey,
  pulseCurrentStep = true,
  error,
  exceptionLabel,
  className,
}: LifecycleStageTrackerProps<TStatus>) {
  const scrollerRef = React.useRef<HTMLDivElement>(null);
  const currentStepRef = React.useRef<HTMLLIElement>(null);
  const currentIndex = steps.findIndex(step => step.key === currentStepKey);
  const trackerStyle: TrackerStyle = { '--lifecycle-step-count': Math.max(steps.length, 1) };
  const classes = ['lifecycle-stage-tracker', className].filter(Boolean).join(' ');
  // Five steps or fewer share a phone's width; longer trackers scroll, so phones also say where the current step is.
  const fits = steps.length <= FIT_STEP_COUNT;

  React.useLayoutEffect(() => {
    let centeringFrame = 0;
    const centerCurrentStep = () => {
      const scroller = scrollerRef.current;
      const currentStep = currentStepRef.current;
      if (!scroller || !currentStep || scroller.scrollWidth <= scroller.clientWidth) return;

      const centeredLeft = currentStep.offsetLeft - ((scroller.clientWidth - currentStep.offsetWidth) / 2);
      const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
      scroller.scrollLeft = Math.min(Math.max(centeredLeft, 0), maxScrollLeft);
    };
    const scheduleCenterCurrentStep = () => {
      window.cancelAnimationFrame(centeringFrame);
      centeringFrame = window.requestAnimationFrame(centerCurrentStep);
    };
    centerCurrentStep();
    scheduleCenterCurrentStep();
    window.addEventListener('resize', scheduleCenterCurrentStep);
    return () => {
      window.cancelAnimationFrame(centeringFrame);
      window.removeEventListener('resize', scheduleCenterCurrentStep);
    };
  }, [currentStepKey, steps.length]);

  return (
    <div
      className={classes}
      aria-label={ariaLabel}
      aria-busy={busy || undefined}
      data-exception={exceptionLabel ? 'true' : undefined}
      data-fits={fits ? 'true' : undefined}
    >
      {!fits && currentIndex >= 0 ? (
        <span className="lifecycle-stage-tracker__position" aria-hidden="true">Step {currentIndex + 1} of {steps.length}</span>
      ) : null}
      <div ref={scrollerRef} className="lifecycle-stage-tracker__scroller">
        <ol className="lifecycle-stage-tracker__steps" style={trackerStyle}>
          {steps.map((step, index) => {
            const isCurrent = index === currentIndex;
            const isComplete = currentIndex >= 0 && index < currentIndex;
            const isPending = pendingStepKey === step.key;
            const unavailable = disabled || busy || isCurrent || !step.targetStatus;
            const state = isCurrent ? 'current' : isComplete ? 'complete' : 'upcoming';
            const baseHelper = isCurrent
              ? `${step.label} is the current milestone${currentStatusLabel ? ` (${currentStatusLabel})` : ''}.`
              : step.targetStatus
                ? onSelect
                  ? `Move to ${step.label}.`
                  : `${step.label} milestone.`
                : `${step.label} is unavailable because its event stage is not configured.`;
            const helper = step.detail ? `${baseHelper} ${step.detail}.` : baseHelper;

            return (
              <li
                key={step.key}
                ref={isCurrent ? currentStepRef : undefined}
                className="lifecycle-stage-tracker__step"
                data-state={state}
                data-busy={isPending ? 'true' : undefined}
                data-current-motion={isCurrent ? (pulseCurrentStep ? 'pulse' : 'static') : undefined}
              >
                {onSelect ? (
                  <button
                    type="button"
                    className="lifecycle-stage-tracker__control"
                    disabled={unavailable}
                    aria-current={isCurrent ? 'step' : undefined}
                    aria-label={helper}
                    title={unavailable && disabledReason && !isCurrent ? disabledReason : helper}
                    onClick={() => {
                      if (!unavailable && step.targetStatus) onSelect(step.targetStatus, step);
                    }}
                  >
                    <span className="lifecycle-stage-tracker__node" aria-hidden="true" />
                    <span className="lifecycle-stage-tracker__label">{step.label}</span>
                    {step.detail ? <span className="lifecycle-stage-tracker__detail">{step.detail}</span> : null}
                  </button>
                ) : (
                  <div
                    className="lifecycle-stage-tracker__control lifecycle-stage-tracker__control--readonly"
                    aria-current={isCurrent ? 'step' : undefined}
                    title={helper}
                  >
                    <span className="lifecycle-stage-tracker__node" aria-hidden="true" />
                    <span className="lifecycle-stage-tracker__label">{step.label}</span>
                    {step.detail ? <span className="lifecycle-stage-tracker__detail">{step.detail}</span> : null}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </div>
      {exceptionLabel ? (
        <span className="lifecycle-stage-tracker__exception" role="status">
          {exceptionLabel}
        </span>
      ) : null}
      {busy ? (
        <span className="lifecycle-stage-tracker__message" role="status" aria-live="polite">
          Updating stage...
        </span>
      ) : error ? (
        <span className="lifecycle-stage-tracker__message lifecycle-stage-tracker__message--error" role="alert">
          {error}
        </span>
      ) : disabledReason && disabled ? (
        <span className="lifecycle-stage-tracker__message">{disabledReason}</span>
      ) : null}
    </div>
  );
}
